import { useTranslation } from 'react-i18next';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { ChevronUp, ChevronDown } from 'lucide-react';
import type { Site, SitesFilters } from '../types';
import type { SiteColumnKey } from './ColumnVisibilityToggle';
import { DEFAULT_VISIBLE_COLUMNS } from './ColumnVisibilityToggle';

interface SitesTableProps {
  sites: Site[];
  sortKey?: SitesFilters['sortKey'];
  sortDirection?: 1 | -1;
  onSort?: (key: SitesFilters['sortKey']) => void;
  onEditClick?: (site: Site) => void;
  page?: number;
  pageSize?: number;
  total?: number;
  onPageChange?: (page: number) => void;
  showAccount?: boolean;
  visibleColumns?: SiteColumnKey[];
}

function getAccountName(site: Site): string {
  if (site.accountName) return site.accountName;
  return '—';
}

function formatAddress(site: Site): string {
  const parts = [
    site.addressLine1 || site.address,
    site.city,
    site.stateProvince || site.state_province_county,
    site.postalCode || site.postcode,
    site.country,
  ].filter(Boolean);

  return parts.length > 0 ? parts.join(', ') : '—';
}

function TagsBadges({ tags }: { tags?: string[] }) {
  if (!tags || tags.length === 0) {
    return <span className="text-muted-foreground">—</span>;
  }

  return (
    <div className="flex flex-wrap gap-1">
      {tags.slice(0, 3).map((tag) => (
        <Badge key={tag} variant="secondary" className="text-xs">
          {tag}
        </Badge>
      ))}
      {tags.length > 3 && (
        <Badge variant="outline" className="text-xs">
          +{tags.length - 3}
        </Badge>
      )}
    </div>
  );
}

function SortIcon({ active, direction }: { active: boolean; direction: 1 | -1 }) {
  if (!active) {
    return <ChevronUp className="w-4 h-4 text-gray-300" />;
  }
  return direction === 1 ? (
    <ChevronUp className="w-4 h-4 text-gray-700" />
  ) : (
    <ChevronDown className="w-4 h-4 text-gray-700" />
  );
}

export function SitesTable({
  sites,
  sortKey,
  sortDirection = 1,
  onSort,
  onEditClick,
  page = 0,
  pageSize = 25,
  total = 0,
  onPageChange,
  showAccount = true,
  visibleColumns = DEFAULT_VISIBLE_COLUMNS,
}: SitesTableProps) {
  const { t } = useTranslation('sites');
  const totalPages = Math.ceil(total / pageSize) || 1;

  const isVisible = (col: SiteColumnKey) => visibleColumns.includes(col);

  const handleSort = (key: SitesFilters['sortKey']) => {
    if (onSort) {
      onSort(key);
    }
  };

  const SortableHeader = ({
    sortKeyVal,
    children,
    className,
  }: {
    sortKeyVal: SitesFilters['sortKey'];
    children: React.ReactNode;
    className?: string;
  }) => (
    <TableHead
      className={`cursor-pointer select-none hover:bg-gray-100 ${className || ''}`}
      onClick={() => handleSort(sortKeyVal)}
    >
      <div className="flex items-center gap-1">
        {children}
        <SortIcon active={sortKey === sortKeyVal} direction={sortDirection} />
      </div>
    </TableHead>
  );

  if (sites.length === 0) {
    return (
      <div className="border rounded-lg p-8 text-center text-muted-foreground">
        {t('table.noResults', 'No sites found matching your filters.')}
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="border rounded-lg overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow className="bg-muted/50">
              {isVisible('name') && (
                <SortableHeader sortKeyVal="name">{t('table.site', 'Site')}</SortableHeader>
              )}
              {showAccount && isVisible('account') && (
                <SortableHeader sortKeyVal="accountName">
                  {t('table.account', 'Account')}
                </SortableHeader>
              )}
              {isVisible('tags') && (
                <TableHead>{t('table.tags', 'Tags')}</TableHead>
              )}
              {isVisible('locations') && (
                <TableHead>{t('table.locations', 'Locations')}</TableHead>
              )}
              {isVisible('address') && (
                <SortableHeader sortKeyVal="city">{t('table.address', 'Address')}</SortableHeader>
              )}
              {isVisible('status') && (
                <SortableHeader sortKeyVal="status">{t('table.status', 'Status')}</SortableHeader>
              )}
              <TableHead className="w-16" />
            </TableRow>
          </TableHeader>
          <TableBody>
            {sites.map((site) => (
              <TableRow
                key={site.id || site._id}
                className="hover:bg-muted/50"
              >
                {isVisible('name') && (
                  <TableCell>
                    <div className="font-medium">{site.name}</div>
                    {site.abbreviations && site.abbreviations.length > 0 && (
                      <div className="text-sm text-muted-foreground">
                        {site.abbreviations.join(', ')}
                      </div>
                    )}
                  </TableCell>
                )}
                {showAccount && isVisible('account') && (
                  <TableCell className="max-w-[180px] truncate">
                    {getAccountName(site)}
                  </TableCell>
                )}
                {isVisible('tags') && (
                  <TableCell className="max-w-[200px]">
                    <TagsBadges tags={site.tags} />
                  </TableCell>
                )}
                {isVisible('locations') && (
                  <TableCell className="max-w-[200px] truncate text-muted-foreground">
                    {site.locationNames && site.locationNames.length > 0
                      ? site.locationNames.join(', ')
                      : '—'}
                  </TableCell>
                )}
                {isVisible('address') && (
                  <TableCell className="max-w-[250px] truncate text-muted-foreground">
                    {formatAddress(site)}
                  </TableCell>
                )}
                {isVisible('status') && (
                  <TableCell>
                    <Badge variant={site.disabled ? 'secondary' : 'default'}>
                      {site.disabled ? 'Inactive' : 'Active'}
                    </Badge>
                  </TableCell>
                )}
                <TableCell className="text-right">
                  <button
                    type="button"
                    onClick={() => onEditClick?.(site)}
                    className="text-atek-blue hover:underline text-sm cursor-pointer"
                  >
                    Edit
                  </button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex items-center justify-between">
          <p className="text-sm text-muted-foreground">
            Page {page + 1} of {totalPages}
          </p>
          <div className="flex gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => onPageChange?.(Math.max(0, page - 1))}
              disabled={page === 0}
            >
              Previous
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => onPageChange?.(Math.min(totalPages - 1, page + 1))}
              disabled={page >= totalPages - 1}
            >
              Next
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
